import {
  getChildTokens,
  getTokenText,
} from '@/pocs/chat/export/formats/markdown-tokens'
import { formatExportedAt } from '@/pocs/chat/export/transcript'
import { Lexer } from 'marked'
import type { ChatTranscript } from '@/pocs/chat/export/types'
import type { Token, Tokens } from 'marked'

const SEPARATOR = '-'.repeat(40)

function inlineToText(tokens: Token[]): string {
  return tokens
    .map((token) => {
      if (token.type === 'br') return '\n'
      const children = getChildTokens(token)
      return children.length ? inlineToText(children) : getTokenText(token)
    })
    .join('')
}

function listToText(list: Tokens.List, depth: number): string {
  const indent = '  '.repeat(depth)
  const start = typeof list.start === 'number' ? list.start : 1
  return list.items
    .map((item, index) => {
      const marker = list.ordered ? `${start + index}.` : '-'
      return `${indent}${marker} ${blocksToText(item.tokens, depth + 1, '\n').trim()}`
    })
    .join('\n')
}

function blocksToText(tokens: Token[], depth = 0, separator = '\n\n'): string {
  return tokens
    .map((token) => {
      switch (token.type) {
        case 'space':
          return ''
        case 'hr':
          return SEPARATOR
        case 'code':
          return (token as Tokens.Code).text
        case 'list':
          return listToText(token as Tokens.List, depth)
        case 'table': {
          const { header, rows } = token as Tokens.Table
          return [header, ...rows]
            .map((cells) =>
              cells.map((cell) => inlineToText(cell.tokens)).join(' | '),
            )
            .join('\n')
        }
        default:
          return inlineToText(getChildTokens(token)) || getTokenText(token)
      }
    })
    .filter(Boolean)
    .join(separator)
}

function markdownToPlainText(markdown: string) {
  return blocksToText(Lexer.lex(markdown))
}

export function toPlainText(transcript: ChatTranscript): string {
  const { title, contextFolders, exchanges, labels } = transcript
  const header = [
    title,
    `${labels.context}: ${contextFolders.join(', ')}`,
    `${labels.generatedBy} · ${formatExportedAt(transcript)}`,
  ].join('\n')
  const body = exchanges.map(({ question, answer, isComplete }) =>
    [
      `${labels.question}:`,
      question,
      '',
      `${labels.answer}:`,
      markdownToPlainText(answer),
      ...(isComplete ? [] : ['', `(${labels.incompleteAnswer})`]),
    ].join('\n'),
  )
  return `${[header, ...body].join(`\n\n${SEPARATOR}\n\n`)}\n`
}
